import React, { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import {
  Button,
  Container,
  Form,
  Input,
  Spinner,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
//import { useDispatch } from "react-redux";
import useGeolocation from "./hooks/useGeolocation"; 
//import { handleLogin, handleError } from "./redux/actions"; 
import {
  validateCredentials,
  clearSessionStorage,
  saveSessionData,
} from "./utils/sessionUtils";
import axios from "axios";
import SignupModal from "./SignupModal";

// Import your existing OTP services
import { sendMobileVeirfyOtpToServer, verifyOtpnonmember } from "./otpServices"; 
import { u } from "framer-motion/client";

// Lazy load the OTP modal
const OtpModal = lazy(() => import("./OTPModal"));

const Login = ({ setUserRights }) => {
  const [signupOpen, setSignupOpen] = useState(false);
  const navigate = useNavigate();

  // Core Login States
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [branch, setBranch] = useState("Kyts");
  const [loading, setLoading] = useState(false);
  const [loadingSignUp, setLoadingSignUp] = useState(false);
  
  // Primary Login OTP flow
  const [modalOpen, setModalOpen] = useState(false);
  const [loginData, setLoginData] = useState(null);

  // Recovery & Reset States
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotMobile, setForgotMobile] = useState("");
  const [forgotType, setForgotType] = useState("password"); // 'password' or 'username'
  const [loadingForgot, setLoadingForgot] = useState(false);
  const [recoveryOtpDetails, setRecoveryOtpDetails] = useState(null);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);

  // New credential inputs
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  const { latitude, longitude, getLocation } = useGeolocation();

  useEffect(() => {
    if (window.location.hostname.includes("kytslibrary")) {
      setBranch("Kyts");
    }
    if (window.location.hostname.includes("localhost")) {
      setBranch("Kyts");
    }
    getLocation();
  }, [getLocation]);

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleRecoveryOtpModal = () => setRecoveryModalOpen(!recoveryModalOpen);
  
  const toggleForgotModal = () => {
    setForgotOpen(!forgotOpen);
    if(forgotOpen) {
      setForgotMobile("");
      setNewUsername("");
      setNewPassword("");
      setIsOtpVerified(false);
      setRecoveryOtpDetails(null);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!branch || !username || !password) {
      alert("Please fill all fields");
      setLoading(false);
      return;
    }

    try {
      const response = await validateCredentials(branch, username, password);
      if (response.error) {
        alert(response.error);
        setLoading(false);
        return;
      }

      if (response.otpSent) {
        setLoginData(response);
        toggleModal();
      } else {
        sessionStorage.setItem("userData", JSON.stringify({ ...response, branch }));
        completeLogin(response);
      }
    } catch (error) {
      console.error("Login Error:", error);
      alert("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Primary Login Verification
  // Primary Login Verification using Axios
  const handleOtpSubmit = async (otp) => {
    try {
      const trimmedOtp = otp?.trim();
      if (!trimmedOtp) {
        alert("OTP cannot be empty.");
        return { error: true, message: "OTP cannot be empty." };
      }

      const apiUrl = `${process.env.REACT_APP_COLLEGE_ERP_API}verify-otp`;
      const requestBody = {
        token: loginData?.otpDetails?.token,
        otp: trimmedOtp,
        id: loginData.userId,
        branch,
      };

      // Axios automatically strings payloads and attaches correct headers
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          Authorization: `Bearer ${loginData?.token}`,
        },
      });

      // Axios returns the payload under response.data
      const res = response.data;

      if (res?.message === "OTP verified successfully" || res?.message === "Verified") {
        completeLogin(res);
        return res; // ✅ Returns the data object to OtpModal
      } else {
        alert("Invalid OTP. Please try again.");
        return res;
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      
      // Extract the custom error message your backend sent down (e.g., "Invalid or expired OTP")
      const backendErrorMessage = error?.response?.data?.message || error?.response?.data?.error;
      
      return { 
        error: true, 
        message: backendErrorMessage || "An error occurred while verifying the OTP. Please try again." 
      };
    }
  };

  // --- RECOVERY FLOW IMPLEMENTATION ---

  // Phase 1: Request an OTP to your mobile verify endpoint
  const handleForgotSubmit = (e) => {
    e.preventDefault();
    setLoadingForgot(true);

    sendMobileVeirfyOtpToServer(forgotMobile,"Kyts Library")
      .then((data) => {
        console.log("Success:", data);
        
        // Match your actual backend object pattern
        if (data && data.success === true) {
          setRecoveryOtpDetails(data);
          toggleRecoveryOtpModal(); 
        } else {
          alert("Could not process OTP request. Verify mobile format.");
        }
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "Failed to send verification SMS.");
        console.error("Failed to send OTP:", error);
      })
      .finally(() => {
        setLoadingForgot(false);
      });
  };

  // Phase 2: Check OTP against the nested otpResponse token
  const handleRecoveryOtpSubmit = (otp) => {
    // Your services/otpService.js verifyOtp reads: token: otpDetails.data.token
    // To make it happy without rewriting the shared file, we forge that shape here:
    const formattedOtpDetails = {
      data: {
        token: recoveryOtpDetails?.otpResponse?.token
      }
    };

    // The endpoint verification needs an Authorization header: `Bearer ${userData.token}`
  

    verifyOtpnonmember(formattedOtpDetails, otp, "customer", "Kyts", forgotMobile)
      .then((res) => {
        alert("Mobile Number Verified Successfully!");
        toggleRecoveryOtpModal(); 
        setIsOtpVerified(true);   // Swaps layout view to Credential Editing state
      })
      .catch((err) => {
        alert(err?.response?.data?.error || "Invalid OTP token validation failure.");
        console.error(err);
      });
  };

  // Phase 3: Update records on the backend
  const handleUpdateCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoadingUpdate(true);

   try {
  const updateUrl = `${process.env.REACT_APP_COLLEGE_ERP_API}update-credentials`;
  
  // Construct the payload based on whether they are recovering username or password
  const payload = forgotType === "username" 
    ? { userName: newUsername } 
    : { password: newPassword, userName:username }; // Include username in password update for identification

  const response = await axios.post(updateUrl, payload, {
    // Axios 'params' automatically converts this to: ?branch=XYZ&mobileNo=123&rights=Subscriber
    params: {
      branch: branch,
      mobileNo: forgotMobile,
      rights: forgotType === "Admin" ? "Admin" : "Subscriber" // Or map your forgotType accordingly
    }
  });

 

      alert(`${forgotType.charAt(0).toUpperCase() + forgotType.slice(1)} modified successfully!`);
      toggleForgotModal();
    } catch (error) {
      console.error("Failed updating parameters:", error);
      alert(error?.response?.data?.message || "Modification pipeline rejected.");
    } finally {
      setLoadingUpdate(false);
    }
  };

  const completeLogin = (data) => {
    const { branch, userName } = data;
    setUserRights(data.rights);
    sessionStorage.setItem("userRights", JSON.stringify(data.rights));
    sessionStorage.setItem("userData", JSON.stringify({ ...data }));
    navigateBasedOnRights(data.rights, branch, userName);
  };

  const navigateBasedOnRights = (rights, branch, userName) => {
    switch (rights) {
      case "Admin":
        navigate("/admin", { state: { branch, userName } });
        break;
      case "Subscriber":
        navigate("/books", { state: { branch, userName } });
        break;
      default:
        break;
    }
  };

  return (
    <Container className="mt-5" style={{ maxWidth: "450px" }}>
      <div className="p-4 shadow-sm rounded bg-white border">
        <h3 className="text-center mb-4">Account Login</h3>
        <Form onSubmit={handleLoginSubmit}>
          <FormGroup>
            <Label for="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUserName(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label for="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormGroup>
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button type="submit" color="primary" disabled={loading} style={{ minWidth: "100px" }}>
              {loading ? <Spinner size="sm" /> : "Login"}
            </Button>
            <Button color="outline-secondary" disabled={loading} onClick={() => setSignupOpen(true)}>
              {loadingSignUp ? <Spinner size="sm" /> : "Sign Up"}
            </Button>
          </div>
        </Form>

        <hr />

        <div className="text-center">
          <span className="text-muted small">Forgot your details? </span>
          <div className="d-flex justify-content-center gap-3 mt-1">
           { 
          //  <Button 
          //    color="link" 
          //    className="p-0 text-decoration-none small"
          //    onClick={() => { setForgotType("username"); toggleForgotModal(); }}
          //  >
          //    Forgot Username
          //  </Button>
            }
          {
        //   <span className="text-muted small">|</span>
          }
            <Button 
              color="link" 
              className="p-0 text-decoration-none small"
              onClick={() => { setForgotType("password"); toggleForgotModal(); }}
            >
              Forgot Password
            </Button>
          </div>
        </div>
      </div>

      {/* Primary Authentication Validation OTP Overlay */}
      <Suspense fallback={<Spinner />}>
        {modalOpen && (
          <OtpModal isOpen={modalOpen} toggle={toggleModal} handleOtpSubmit={handleOtpSubmit} />
        )}
      </Suspense>

      {/* Recovery Flow Verification OTP Overlay */}
      <Suspense fallback={<Spinner />}>
        {recoveryModalOpen && (
          <OtpModal isOpen={recoveryModalOpen} toggle={toggleRecoveryOtpModal} handleOtpSubmit={handleRecoveryOtpSubmit} />
        )}
      </Suspense>

      {/* Sign Up Modal */}
      <SignupModal isOpen={signupOpen} toggle={() => setSignupOpen(false)} branch={branch} />

      {/* Recovery / Modification Modal Interface */}
      <Modal isOpen={forgotOpen} toggle={toggleForgotModal} centered>
        <ModalHeader toggle={toggleForgotModal}>
          {isOtpVerified ? `Update ${forgotType === "password" ? "Password" : "Username"}` : `Verify Account`}
        </ModalHeader>
        
        {!isOtpVerified ? (
          // STEP 1: Enter Mobile to Request Verification Check
          <Form onSubmit={handleForgotSubmit}>
            <ModalBody>
              <p className="text-muted small mb-3">
                Verify your identity. Enter your registered mobile number below to receive a security verification code.
              </p>
              <FormGroup>
                <Label for="forgotMobile">Registered Mobile Number</Label>
                <Input
                  id="forgotMobile"
                  type="tel"
                  pattern="[0-9]{10}"
                  placeholder="Enter 10-digit mobile number"
                  value={forgotMobile}
                  onChange={(e) => setForgotMobile(e.target.value)}
                  required
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={toggleForgotModal}>Cancel</Button>
              <Button color="primary" type="submit" disabled={loadingForgot}>
                {loadingForgot ? <Spinner size="sm" /> : "Send Verification OTP"}
              </Button>
            </ModalFooter>
          </Form>
        ) : (
          // STEP 2: Render modification form inputs after verification flag validates
          <Form onSubmit={handleUpdateCredentialsSubmit}>
            <ModalBody>
              <p className="text-success small mb-3">
                ✓ Mobile verification complete. Please establish your new credentials details below.
              </p>
              {forgotType === "username" ? (
                <FormGroup>
                  <Label for="newUsername">New Username</Label>
                  <Input
                    id="newUsername"
                    type="text"
                    placeholder="Enter new username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    required
                  />
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label for="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Enter new secure password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={toggleForgotModal}>Close</Button>
              <Button color="success" type="submit" disabled={loadingUpdate}>
                {loadingUpdate ? <Spinner size="sm" /> : "Save Changes"}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Container>
  );
};

export default Login;